import { useTranslation } from "react-i18next";
import { Dropdown } from "src/common/components/Input";
import { profileRowClassName } from "src/common/components/Input/ProfileInputStyle";
import type { AccessibilityColorBlindMode, IAccessibilityPreferences } from "src/model/model";

export const DEFAULT_ACCESSIBILITY_PRESET: IAccessibilityPreferences = {
  highContrast: false,
  colorBlindMode: "none",
  dwellClick: false,
  dwellSeconds: 1.5,
  noTimers: false,
  reducedMotion: false,
};

const COLOR_BLIND_MODES: AccessibilityColorBlindMode[] = ["none", "deutan", "protan", "tritan"];
const BOOLEAN_KEYS = ["highContrast", "noTimers", "reducedMotion", "dwellClick"] as const;

const isDefaultPreset = (preset: IAccessibilityPreferences) =>
  (
    Object.keys(DEFAULT_ACCESSIBILITY_PRESET) as (keyof typeof DEFAULT_ACCESSIBILITY_PRESET)[]
  ).every((key) => preset[key] === DEFAULT_ACCESSIBILITY_PRESET[key]);

export const asAccessibilityPreset = (
  value: IAccessibilityPreferences | null | undefined,
): IAccessibilityPreferences | null =>
  value == null || isDefaultPreset({ ...DEFAULT_ACCESSIBILITY_PRESET, ...value }) ? null : value;

const checkboxClassName = "rounded border-gray-300 text-green-600 shadow-sm focus:ring-green-500";
const labelClassName = "w-full sm:w-1/3 text-sm text-center sm:text-left";

interface IAccessibilityPresetSectionProps {
  value: IAccessibilityPreferences | null;
  onChange: (next: IAccessibilityPreferences | null) => void;
}

export const AccessibilityPresetSection = ({
  value,
  onChange,
}: IAccessibilityPresetSectionProps) => {
  const { t } = useTranslation();
  const current = { ...DEFAULT_ACCESSIBILITY_PRESET, ...(value ?? {}) };

  const sourceKey =
    value?.source === "org_preset"
      ? "sourceOrgPreset"
      : value?.source === "device"
      ? "sourceDevice"
      : value?.source === "user"
      ? "sourceUser"
      : null;

  const update = (patch: Partial<IAccessibilityPreferences>) => {
    const next = { ...current, ...patch };
    onChange(isDefaultPreset(next) ? null : next);
  };

  const colorBlindOptions = COLOR_BLIND_MODES.map((mode) => ({
    value: mode,
    optionText: t(`pages.participants.accessibility.${mode === "none" ? "colorBlindNone" : mode}`),
  }));

  return (
    <div className="w-full" data-testid="accessibility-preset-section">
      <h3 className="py-3">{t("pages.participants.accessibility.title")}</h3>
      <p className="text-sm text-gray-500 pb-2">
        {t("pages.participants.accessibility.learnerCanOverride")}
      </p>

      {sourceKey && (
        <p className="text-sm text-gray-500 italic pb-2" data-testid="accessibility-preset-source">
          {t(`pages.participants.accessibility.${sourceKey}`)}
        </p>
      )}

      <div className={profileRowClassName}>
        <p className={labelClassName}>{t("pages.participants.accessibility.colorBlindMode")}</p>
        <div className="flex grow justify-end">
          <Dropdown
            label=""
            labelFontClassName="text-sm"
            options={colorBlindOptions}
            asDefaultValue
            defaultValueIndex={COLOR_BLIND_MODES.indexOf(current.colorBlindMode ?? "none")}
            data-testid="accessibility-preset-colorBlindMode"
            onChangeCb={(mode) => update({ colorBlindMode: mode as AccessibilityColorBlindMode })}
          />
        </div>
      </div>

      {BOOLEAN_KEYS.map((key) => (
        <div className={profileRowClassName} key={key}>
          <p className={labelClassName}>{t(`pages.participants.accessibility.${key}`)}</p>
          <div className="flex grow justify-end">
            <input
              type="checkbox"
              className={checkboxClassName}
              data-testid={`accessibility-preset-${key}`}
              checked={current[key] === true}
              onChange={(e) => update({ [key]: e.target.checked })}
            />
          </div>
        </div>
      ))}

      <div className={profileRowClassName}>
        <p className={labelClassName}>{t("pages.participants.accessibility.dwellSeconds")}</p>
        <div className="flex grow justify-end">
          <input
            type="number"
            min={1}
            max={3}
            step={0.5}
            className="rounded border-2 border-solid border-gray-300 p-2 w-24 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="accessibility-preset-dwellSeconds"
            value={current.dwellSeconds ?? 1.5}
            disabled={!current.dwellClick}
            onChange={(e) => {
              const seconds = Number(e.target.value);
              if (Number.isFinite(seconds)) {
                update({ dwellSeconds: Math.min(3, Math.max(1, seconds)) });
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};
